import type { UploadTelemetry } from "./ports";

export * from "./uploadChunk";
export * from "./uploadDeadline";
export * from "./ports";
export * from "./completionTransition";
export * from "./resumeSession";
export * from "./targetInitiation";
export * from "./uploadTelemetry";
export * from "./useScreenWakeLock";
export * from "./drainEngine";
export * from "./reconciliationStateMachine";
export * from "./ownershipGate";
export * from "./chunkedUploadDriver";
export * from "./driverControlFlow";
export * from "./driverChunkLoop";
export * from "./partReceiptRecovery";
export * from "./driverResume";
export * from "./partsIncompleteBound";
export * from "./driverUrlRefresh";
export * from "./refusedAttemptProbe";
export * from "./multipartValidation";
export * from "./persistedChunkIntegrity";
export * from "./cloudHostProbe";
export * from "./indexedDbErrors";
export * from "./legacyChunkMetadata";
export * from "./concurrency";
export * from "./chunkSize";
export * from "./isobmffLayout";
export * from "./chunkManifest";
export * from "./chunkedObjectVideoPlan";
export * from "./persistedChunkManifest";
export * from "./chunkedObjectLaneColumns";
export * from "./durableUploadSchema";
export * from "./durableOwnership";
export * from "./retryPolicy";
export * from "./authGate";
export * from "./uploadId";
export * from "./drainHostProtocol";
export * from "./sourceReadStrategy";
export * from "./sequentialStreamSource";
export * from "./recordingWire";
export * from "./recordingChunkStreamWire";
export * from "./persistTimeout";
export * from "./poisonBudget";
export * from "./settlingCeiling";
export * from "./schedulerLease";
export * from "./chunkStreamUploader";
export * from "./smallFileLane";
export * from "./producerCaptureLease";
export * from "./blobPutTransport";
export * from "./documentVisibility";
export * from "./uploadsFacadeClient";
export * from "./uploadFacadeErrors";
export * from "./browserConnectivityPort";
export * from "./periodicWake";
export * from "./liveSnapshotUploadLane";
export * from "./passiveLeavingHint";

/**
 * Sizes already reported as unusable, so a bad row logs once instead of on every re-render. Bounded
 * because the keys come from untrusted stored values — an unbounded set would grow with them.
 */
const warnedUnusableSizes = new Set<string>();
const WARNED_UNUSABLE_SIZES_LIMIT = 20;

/**
 * Formats a byte count with the largest unit that keeps it above 1 (B/KB/MB).
 *
 * LSP-17969: this is the single formatter for every user-visible upload size label. Call sites must
 * NOT append a unit — the returned string carries its own.
 *
 * The declared type says `number`, but real callers pass values from unvalidated storage: resumed
 * upload rows read `size` out of IndexedDB records written by older app versions, so `null` /
 * `undefined` / `NaN` genuinely reach here. Unguarded, `null` interpolates as the literal user-facing
 * string "null B" and `undefined` as "NaN MB". Return a neutral placeholder and warn once instead —
 * a wrong size on screen with nothing in the logs is how LSP-17969 stayed invisible.
 */
export function formatFileSize(bytes: number): string {
	// Only a number, bigint, or non-blank numeric string is a size. Stored records can hold a numeric
	// string, which the old MB-only math turned into "0.00 MB", so those are worth coercing — but
	// `Number()` is far too eager for anything else: it maps `true` to 1, `[]` to 0 and `[512]` to 512,
	// which is how a corrupt stored value becomes a *plausible* label ("1 B") instead of an obviously
	// bad one. A plausible wrong size is the exact failure this ticket exists to remove. `null` and ""
	// are rejected for the same reason (both coerce to 0, and "0 B" for an unknown size is a lie).
	// No try/catch around the coercion, deliberately: the two kinds of value `Number()` can throw or
	// run arbitrary code for — a symbol, and an object with a hostile `valueOf` — fail this gate and
	// take the `NaN` branch without `Number()` ever being called. No *input* can reach it that throws,
	// since the three types that do reach it (number, bigint, non-blank string) never do. (Sabotaging
	// the global `Number` intrinsic would, but that breaks `Number.isFinite` on the next line and
	// every other module too — see the skipped round-6 case in
	// tests/format-file-size.breakit-review-feedback.spec.ts.)
	const isCoercible = typeof bytes === "number" || typeof bytes === "bigint";
	const isNumericString = typeof bytes === "string" && (bytes as string).trim() !== "";
	const byteCount = isCoercible || isNumericString ? Number(bytes) : NaN;
	if (!Number.isFinite(byteCount) || byteCount < 0) {
		// `String()` runs an arbitrary `toString`, so describing the bad value must not itself throw —
		// the whole point of this branch is that a bad size never breaks the render.
		let key: string;
		try {
			key = String(bytes);
		} catch {
			key = `<unprintable ${typeof bytes}>`;
		}
		if (!warnedUnusableSizes.has(key) && warnedUnusableSizes.size < WARNED_UNUSABLE_SIZES_LIMIT) {
			warnedUnusableSizes.add(key);
			console.warn(`[Upload] Cannot format file size: expected a non-negative finite byte count, got ${key}`);
		}
		return "—";
	}
	if (byteCount < 1024) return `${byteCount} B`;
	if (byteCount < 1024 * 1024) return `${(byteCount / 1024).toFixed(1)} KB`;
	return `${(byteCount / (1024 * 1024)).toFixed(1)} MB`;
}

export async function logStorageEstimate(telemetry: UploadTelemetry): Promise<void> {
	if (!navigator.storage?.estimate) return;
	try {
		const { quota, usage } = (await navigator.storage.estimate()) as {
			quota: number;
			usage: number;
		};
		telemetry.logInfo(
			`[Upload] Storage: ${formatFileSize(usage)} used / ${formatFileSize(quota)} quota (${((usage / quota) * 100).toFixed(1)}% full)`
		);
	} catch {
		// Storage API unavailable — silently skip.
	}
}

// LSP-20745: kept below the storage helper so its Safari-floor baseline line numbers do not move.
export * from "./pngToJpeg";
export * from "./pickedFileReadGate";
export * from "./recordingLanes";
// LSP-23046: the once-per-call zoom_session_env storage baseline; appended last so no Safari-floor baseline line moves.
export { probeStorageEstimate } from "./liveSnapshotStorageDiagnostics";

export * from "./uploadObjectManifest";
export * from "./uploadObjectContract";

export * from "./uploadKnownSizeControl";
export * from "./uploadVerificationPending";

export * from "./knownSizeUploadFlow";
